import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { tr } from '../../i18n/i18n';
import { InputNumber } from 'primereact/inputnumber';
import { TabView, TabPanel } from 'primereact/tabview';
import { AppButton } from '../atoms';
import { ConfirmationModal, ModalHandle, PrimaryModal } from '../Modals';
import { useAppSelector, useAppDispatch } from '../../redux/stateHooks';
import { endSession } from '../../redux/action/payment.action';
import { formatCurrency } from '../../utility/currencyFormatter';

interface EndSessionModalProps {
  // No visible or onHide props - controlled via ref
}

interface FormData {
  actualCashCount: number | null;
}

interface FormErrors {
  actualCashCount?: string;
}

/**
 * EndSessionModal Component
 *
 * Purpose: Two-tab modal for ending payment session with cash count validation
 * Tab 1: Session summary (readonly)
 * Tab 2: Actual cash count input
 * Uses ConfirmationModal with variance warning before final submission
 *
 * Pattern: Uses PrimaryModal with forwardRef (matches ConfirmationModal standard)
 *
 * Figma Summary: https://www.figma.com/design/gUiYdpQpnM6DFz0BYNSt4a/Classveew-product?node-id=2425-2067
 * Figma Cash Count: https://www.figma.com/design/gUiYdpQpnM6DFz0BYNSt4a/Classveew-product?node-id=5540-16624
 */
const EndSessionModal = forwardRef<ModalHandle, EndSessionModalProps>((props, ref) => {
  const dispatch = useAppDispatch();
  const confirmModalRef = useRef<ModalHandle>(null);
  const { activeSession, loadingEnd } = useAppSelector((state) => state.paymentSession);
  const isLoading = loadingEnd;

  // Get organization currency from localStorage (set during login)
  const orgCurrency = localStorage.getItem('currency') || 'USD';

  const [activeIndex, setActiveIndex] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    actualCashCount: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Calculate expected closing balance
  // Expected = Opening + Cash Collected - Cash Refunded
  const expectedClosingBalance = activeSession
    ? activeSession.openingBalance +
      (activeSession.totalCollected || 0) -
      (activeSession.totalRefunded || 0)
    : 0;

  // Calculate variance
  const variance =
    formData.actualCashCount !== null ? formData.actualCashCount - expectedClosingBalance : 0;

  const hasVariance = Math.abs(variance) > 0.01; // Tolerance for floating point

  // Reset form helper
  const resetForm = () => {
    setActiveIndex(0);
    setFormData({ actualCashCount: null });
    setErrors({});
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    if (ref && typeof ref !== 'function' && ref.current) {
      ref.current.closeModal();
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (formData.actualCashCount === null || formData.actualCashCount < 0) {
      newErrors.actualCashCount = tr('fe_payment_session.please_enter_a_valid_cash_count_must_be_0', 'Please enter a valid cash count (must be 0 or greater)');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle tab navigation
  const handleNext = () => {
    if (activeIndex === 0) {
      setActiveIndex(1);
    }
  };

  const handleBack = () => {
    if (activeIndex === 1) {
      setActiveIndex(0);
    }
  };

  // Handle form submission (Phase 1: Validation)
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // Open confirmation modal
    confirmModalRef.current?.openModal();
  };

  // Handle confirmed submission (Phase 2: API Call)
  const handleConfirmedSubmit = async () => {
    if (!activeSession || formData.actualCashCount === null) return;

    try {
      await dispatch(
        endSession({
          sessionId: activeSession.sessionId,
          data: {
            actual_cash_count: formData.actualCashCount,
          },
        }),
      ).unwrap();

      confirmModalRef.current?.closeModal();
      handleClose(); // Close EndSessionModal via ref
    } catch (error: any) {
      // Error already handled by Redux slice
      confirmModalRef.current?.closeModal();
    }
  };

  // Calculate session duration
  const getSessionDuration = (): string => {
    if (!activeSession) return '0 hours';

    const startTime = new Date(activeSession.startTime);
    const now = new Date();
    const diffMs = now.getTime() - startTime.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMinutes} min`;
    }
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  };

  // Loading state content
  if (!activeSession) {
    return (
      <PrimaryModal
        ref={ref}
        width="768px"
        showHeader={false}
        onClose={handleClose}
        content={
          <div className="flex flex-col gap-4">
            {/* Loading Header */}
            <div className="flex flex-col gap-1">
              <span className="text-xl font-semibold text-gray-900">{tr('fe_payment_session.close_cashier_session', "Close Cashier Session")}</span>
            </div>

            {/* Loading Spinner */}
            <div className="flex flex-col items-center justify-center py-12">
              <i className="pi pi-spin pi-spinner text-4xl text-purple-600 mb-4" />
              <p className="text-gray-600">{tr('fe_payment_session.loading_session_data', "Loading session data...")}</p>
            </div>
          </div>
        }
      />
    );
  }

  // Main content with tabs
  return (
    <>
      <PrimaryModal
        ref={ref}
        width="768px"
        showHeader={false}
        onClose={handleClose}
        content={
          <div className="flex flex-col gap-4">
            {/* Modal Header */}
            <div className="flex flex-col gap-1">
              <span className="text-xl font-semibold text-gray-900">{tr('fe_payment_session.close_cashier_session', "Close Cashier Session")}</span>
              <span className="text-sm text-gray-600">
                {tr('fe_payment_session.balance_your_cash_drawer_and_end_your_shift', "Balance your cash drawer and end your shift")}
              </span>
            </div>

            {/* TabView with Summary and Cash Count tabs */}
            <TabView
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
              pt={{
                inkbar: { className: 'bg-purple-600' },
              }}
            >
          {/* Tab 1: Session Summary */}
          <TabPanel header={tr('fe_payment_session.summary', "Summary")}>
            <div className="flex flex-col gap-4 p-4">
              {/* Session Summary Card */}
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">{tr('fe_payment_session.session_summary', "Session Summary")}</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{tr('fe_payment_session.opening_balance', "Opening Balance")}</p>
                    <p className="font-medium text-gray-900">
                      {formatCurrency(activeSession.openingBalance)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">{tr('fe_payment_session.cash_collected', "Cash Collected")}</p>
                    <p className="font-medium text-green-700">
                      +{formatCurrency(activeSession.totalCollected || 0)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">{tr('fe_payment_session.cash_refunded', "Cash Refunded")}</p>
                    <p className="font-medium text-red-700">
                      -{formatCurrency(activeSession.totalRefunded || 0)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">{tr('fe_payment_session.expected_system_balance', "Expected System Balance")}</p>
                    <p className="font-semibold text-purple-700 text-lg">
                      {formatCurrency(expectedClosingBalance)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Session Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{tr('fe_payment_session.started', "Started")}</p>
                    <p className="font-medium text-gray-900">
                      {new Date(activeSession.startTime).toLocaleString('en-US', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{tr('fe_payment_session.duration', "Duration")}</p>
                    <p className="font-medium text-gray-900">{getSessionDuration()}</p>
                  </div>
                </div>
              </div>

              {activeSession.notes?.start && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-1">{tr('fe_payment_session.session_notes', "Session Notes")}</p>
                  <p className="text-sm text-blue-700">{activeSession.notes.start}</p>
                </div>
              )}
            </div>
          </TabPanel>

          {/* Tab 2: Cash Count */}
          <TabPanel header={tr('fe_payment_session.cash_count', "Cash count")}>
            <div className="flex flex-col gap-4 p-4">
              {/* Session Info Box */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="pi pi-info-circle text-purple-600" />
                  <p className="font-medium text-purple-900">{tr('fe_payment_session.session_information', "Session Information")}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-purple-700 mb-1">{tr('fe_payment_session.started', "Started")}</p>
                    <p className="font-medium text-purple-900">
                      {new Date(activeSession.startTime).toLocaleString('en-US', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-700 mb-1">{tr('fe_payment_session.duration', "Duration")}</p>
                    <p className="font-medium text-purple-900">{getSessionDuration()}</p>
                  </div>
                </div>
              </div>

              {/* Actual Cash Count Input */}
              <div className="flex flex-col gap-2">
                <label htmlFor="actualCashCount" className="text-sm font-medium text-gray-700">
                  Actual Cash Count <span className="text-red-500">*</span>
                </label>
                <InputNumber
                  id="actualCashCount"
                  value={formData.actualCashCount}
                  onValueChange={(e) => {
                    setFormData({ actualCashCount: e.value });
                    if (errors.actualCashCount) {
                      setErrors({ actualCashCount: undefined });
                    }
                  }}
                  mode="currency"
                  currency={orgCurrency}
                  locale="en-US"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  placeholder="0.00"
                  className={`w-full ${errors.actualCashCount ? 'p-invalid' : ''}`}
                  disabled={isLoading}
                />
                {errors.actualCashCount && (
                  <small className="text-red-500">{errors.actualCashCount}</small>
                )}
                <small className="text-gray-500">{tr('fe_payment_session.count_all_cash_currently_in_your_drawer', "Count all cash currently in your drawer")}</small>
              </div>

              {/* Variance Preview (if entered) */}
              {formData.actualCashCount !== null && (
                <div
                  className={`border rounded-lg p-4 ${
                    hasVariance ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <i
                      className={`pi ${
                        hasVariance ? 'pi-exclamation-triangle' : 'pi-check-circle'
                      } ${
                        hasVariance ? 'text-yellow-600' : 'text-green-600'
                      } text-xl flex-shrink-0 mt-0.5`}
                    />
                    <div className="flex-1">
                      <p
                        className={`font-medium mb-1 ${
                          hasVariance ? 'text-yellow-900' : 'text-green-900'
                        }`}
                      >
                        {hasVariance ? 'Variance Detected' : 'Perfect Match'}
                      </p>
                      <p
                        className={`text-sm ${hasVariance ? 'text-yellow-800' : 'text-green-800'}`}
                      >
                        Expected: {formatCurrency(expectedClosingBalance)} | Actual:{' '}
                        {formatCurrency(formData.actualCashCount)}
                      </p>
                      {hasVariance && (
                        <p className="text-sm text-yellow-900 font-medium mt-2">
                          Variance: {variance > 0 ? '+' : ''}
                          {formatCurrency(variance)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabPanel>
        </TabView>

        {/* Footer with conditional buttons */}
        <div className="flex gap-2 justify-end mt-4">
          {activeIndex === 0 && (
            <>
              <AppButton label={tr('fe_payment_session.cancel', "Cancel")} outlined onClick={handleClose} disabled={isLoading} />
              <AppButton
                label={tr('fe_payment_session.next', "Next")}
                icon="pi pi-arrow-right"
                iconPos="right"
                onClick={handleNext}
                disabled={isLoading}
              />
            </>
          )}
          {activeIndex === 1 && (
            <>
              <AppButton label={tr('fe_payment_session.back', "Back")} outlined onClick={handleBack} disabled={isLoading} />
              <AppButton label={tr('fe_payment_session.cancel', "Cancel")} outlined onClick={handleClose} disabled={isLoading} />
              <AppButton
                label={tr('fe_payment_session.close_session', "Close Session")}
                icon="pi pi-check"
                onClick={handleSubmit}
                disabled={isLoading}
                loading={isLoading}
                severity="danger"
              />
            </>
          )}
        </div>
      </div>
    }
  />

      {/* Confirmation Modal (Variance Warning) */}
      <ConfirmationModal
        ref={confirmModalRef}
        title={tr('fe_payment_session.confirm_session_close', "Confirm Session Close")}
        severity={hasVariance ? 'warning' : 'primary'}
        icon={hasVariance ? 'pi-exclamation-triangle' : 'pi-check-circle'}
        yesLabel={tr('fe_payment_session.yes_close_session', "Yes, Close Session")}
        noLabel={tr('fe_payment_session.cancel', "Cancel")}
        onYes={handleConfirmedSubmit}
        onNo={() => confirmModalRef.current?.closeModal()}
        content={
          <div className="flex flex-col gap-3">
            {hasVariance && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                <p className="text-sm font-medium text-yellow-900 mb-1 flex items-center gap-2">
                  <i className="pi pi-exclamation-triangle" />
                  {tr('fe_payment_session.cash_variance_detected', "Cash Variance Detected")}
                </p>
                <p className="text-sm text-yellow-800">
                  Variance: <span className="font-bold">{formatCurrency(Math.abs(variance))}</span>
                </p>
                <p className="text-xs text-yellow-700 mt-2">
                  {tr('fe_payment_session.please_verify_your_cash_count_if_correct_proceed_to', "Please verify your cash count. If correct, proceed to close the session.")}
                </p>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-3">{tr('fe_payment_session.session_summary', "Session Summary")}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">{tr('fe_payment_session.opening_balance', "Opening Balance")}</p>
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(activeSession.openingBalance)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">{tr('fe_payment_session.cash_collected', "Cash Collected")}</p>
                  <p className="font-semibold text-green-700">
                    +{formatCurrency(activeSession.totalCollected || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">{tr('fe_payment_session.actual_cash_count', "Actual Cash Count")}</p>
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(formData.actualCashCount || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">{tr('fe_payment_session.variance', "Variance")}</p>
                  <p
                    className={`font-bold ${
                      hasVariance
                        ? variance > 0
                          ? 'text-green-700'
                          : 'text-red-700'
                        : 'text-gray-900'
                    }`}
                  >
                    {variance > 0 ? '+' : ''}
                    {formatCurrency(variance)}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              {hasVariance
                ? 'Closing this session will log you out and prevent further payment collection until you start a new session.'
                : tr('g.x_are_you_sure_you_want_to_close_this_session', 'Are you sure you want to close this session? You will be logged out.')}
            </p>
          </div>
        }
      />
    </>
  );
},
);

EndSessionModal.displayName = 'EndSessionModal';

export default EndSessionModal;
